import React from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';

import type {
  ChartSyncReviewResponse,
  SyncReviewChange,
} from '../../services/api/types.gen';
import {
  formatDateInAgencyZone,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import {
  SYNC_REVIEW_ACTION_COPY,
  SYNC_REVIEW_ACTION_LABEL,
  SYNC_REVIEW_DESCRIPTION,
  SYNC_REVIEW_EMPTY_COPY,
  SYNC_REVIEW_FAMILIES_TITLE,
  SYNC_REVIEW_FINDINGS_TITLE,
  SYNC_REVIEW_GROUP_TITLES,
  SYNC_REVIEW_LABEL,
  SYNC_REVIEW_OTHER_GROUP_TITLE,
  familyAddedLine,
  lastClosedLine,
} from './syncReviewCopy';

interface SyncReviewDialogProps {
  review: ChartSyncReviewResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ChangeGroup {
  key: string;
  title: string;
  changes: SyncReviewChange[];
}

/**
 * The changes grouped by kind, in the order the backend ranks them. A kind
 * this screen has no heading for is grouped under "Other changes" rather
 * than dropped: a newer refresh may record kinds this build predates.
 */
function groupChanges(changes: SyncReviewChange[]): ChangeGroup[] {
  const groups: ChangeGroup[] = [];
  const byKey = new Map<string, ChangeGroup>();
  for (const change of changes) {
    const known = change.kind in SYNC_REVIEW_GROUP_TITLES;
    const key = known ? change.kind : 'other';
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        title: known
          ? SYNC_REVIEW_GROUP_TITLES[change.kind]
          : SYNC_REVIEW_OTHER_GROUP_TITLE,
        changes: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.changes.push(change);
  }
  return groups;
}

const ChangeRow: React.FC<{ change: SyncReviewChange }> = ({ change }) => {
  const date = change.visit_date
    ? formatDateToMMDDYYYY(change.visit_date)
    : null;
  const hasValues = change.before || change.after;
  return (
    <li className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
      {change.action && (
        <Badge variant='destructive' className='whitespace-nowrap'>
          {SYNC_REVIEW_ACTION_LABEL}
        </Badge>
      )}
      <span className='min-w-0 break-words'>
        {change.visit_label}
        {date && <span className='text-muted-foreground'> · {date}</span>}
      </span>
      {hasValues && (
        <span className='text-muted-foreground'>
          {change.before ?? '—'} → {change.after ?? '—'}
        </span>
      )}
    </li>
  );
};

/**
 * What the External EMR changed on a moved chart since the review was last
 * closed. Reading it is the whole job: closing the dialog is what clears it,
 * so there is no separate "mark reviewed" control.
 */
const SyncReviewDialog: React.FC<SyncReviewDialogProps> = ({
  review,
  open,
  onOpenChange,
}) => {
  const changes = review.changes ?? [];
  const families = review.families ?? [];
  const findings = review.findings ?? [];
  const groups = groupChanges(changes);
  const hasAction = changes.some(change => change.action);
  const isEmpty =
    changes.length === 0 && families.length === 0 && findings.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{SYNC_REVIEW_LABEL}</DialogTitle>
          <DialogDescription>{SYNC_REVIEW_DESCRIPTION}</DialogDescription>
          {review.last_reviewed_at && (
            <p className='text-xs text-muted-foreground'>
              {lastClosedLine(
                formatDateInAgencyZone(review.last_reviewed_at),
                review.last_reviewed_by_name
              )}
            </p>
          )}
        </DialogHeader>

        {isEmpty ? (
          <p className='text-sm text-muted-foreground'>
            {SYNC_REVIEW_EMPTY_COPY}
          </p>
        ) : (
          <div className='space-y-4'>
            {findings.length > 0 && (
              <section aria-label={SYNC_REVIEW_FINDINGS_TITLE}>
                <h3 className='text-sm font-semibold mb-1'>
                  {SYNC_REVIEW_FINDINGS_TITLE} ({findings.length})
                </h3>
                <ul className='space-y-1'>
                  {findings.map(finding => (
                    <li
                      key={`${finding.section}-${finding.message}`}
                      className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'
                    >
                      <Badge
                        variant='destructive'
                        className='whitespace-nowrap'
                      >
                        {SYNC_REVIEW_ACTION_LABEL}
                      </Badge>
                      <span className='min-w-0 break-words'>
                        {finding.message}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {hasAction && <p className='text-sm'>{SYNC_REVIEW_ACTION_COPY}</p>}
            {groups.map(group => (
              <section key={group.key} aria-label={group.title}>
                <h3 className='text-sm font-semibold mb-1'>
                  {group.title} ({group.changes.length})
                </h3>
                <ul className='space-y-1'>
                  {group.changes.map((change, index) => (
                    <ChangeRow
                      key={`${change.kind}-${change.visit_id ?? index}`}
                      change={change}
                    />
                  ))}
                </ul>
              </section>
            ))}
            {families.length > 0 && (
              <section aria-label={SYNC_REVIEW_FAMILIES_TITLE}>
                <h3 className='text-sm font-semibold mb-1'>
                  {SYNC_REVIEW_FAMILIES_TITLE}
                </h3>
                <ul className='space-y-1 text-sm'>
                  {families.map(family => (
                    <li key={family.family}>
                      {familyAddedLine(family.label, family.added)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncReviewDialog;
